import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardMedia,
  CardContent,
  useTheme as useMuiTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import SchoolIcon from '@mui/icons-material/School';
import EventIcon from '@mui/icons-material/Event';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

// Styled components
const StyledTabs = styled(Tabs)(({ theme, isMobile }) => ({
  marginBottom: theme.spacing(6),
  ...(isMobile && {
    borderRadius: '20px',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(240, 240, 250, 0.5)',
    padding: '6px',
    width: '100%',
    maxWidth: '100%',
  }),
  '& .MuiTabs-indicator': {
    display: isMobile ? 'none' : 'block',
    backgroundColor: theme.palette.primary.main,
    height: 3,
  },
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
    width: isMobile ? '100%' : 'auto',
  },
  '& .MuiTabs-scroller': {
    overflow: isMobile ? 'auto !important' : 'hidden',
    width: isMobile ? '100%' : 'auto',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(4),
    width: '100%',
  },
}));

const StyledTab = styled(Tab)(({ theme, isMobile }) => ({
  minWidth: isMobile ? '100px' : '120px',
  flex: isMobile ? '0 0 auto' : 'none',
  ...(isMobile ? {
    borderRadius: '16px',
    padding: '8px 12px',
    margin: '0 3px',
    whiteSpace: 'nowrap',
    fontSize: '0.75rem',
  } : {
    padding: '12px 16px',
  }),
  fontWeight: 600,
  textTransform: 'none',
  transition: 'all 0.3s ease',
  fontSize: '1rem',
  color: theme.palette.text.primary,
  '&.Mui-selected': {
    ...(isMobile ? {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      boxShadow: '0 4px 10px rgba(106, 73, 219, 0.3)',
    } : {
      color: theme.palette.primary.main,
    }),
  },
  '& .MuiSvgIcon-root': {
    fontSize: isMobile ? '1rem' : '1.4rem',
    marginRight: isMobile ? '4px' : '6px',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '100px',
    padding: '8px 10px',
    fontSize: '0.75rem',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.card,
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.2)',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    borderRadius: '16px',
  },
}));

const StyledImage = styled(CardMedia)(({ theme }) => ({
  height: 0,
  paddingTop: '56.25%', // 16:9 aspect ratio
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    paddingTop: '75%', // 4:3 aspect ratio for mobile (better viewing)
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '2px',
  },
}));

const ExtraCurricular = () => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const activities = [
    {
      id: 'mentorship',
      title: 'Mentorship',
      icon: <SchoolIcon />,
      image: process.env.PUBLIC_URL +'/Images/Saadhana.JPG',
      description: "Saadhana, an annual cultural extravaganza at NUS TLS, serves as a platform for pre-university students to delve into the university experience. As part of this mentorship program, I took on the role of guiding and allocating manpower during the 2024 event, ensuring its success while imparting valuable insights to aspiring students about university life and cultural diversity."
    },
    {
      id: 'event-organizer',
      title: 'Event Organizer',
      icon: <EventIcon />,
      image: process.env.PUBLIC_URL + '/Images/ECE_orientation.JPG',
      description: "As part of the logistics crew for the NUS ECE Orientation Camp, I helped make the chaos feel seamless — from setting up spaces to sorting out meals. It was a great way to give back and help new students feel right at home from day one."
    },
    {
      id: 'hackathons',
      title: 'Hackathons',
      icon: <EmojiEventsIcon />,
      image: process.env.PUBLIC_URL + '/Images/Hackathons.jpg',
      description: "Hackathons are my creative playground. I love the buzz of late-night coding, rapid problem-solving, and building cool things with awesome people. From NUS Hack&Roll to Catalyst AI, placing in the top 10 has been a blast — and a boost to both my skills and caffeine tolerance."
    }
  ];

  // Animation variants
  const tabContentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <Box 
      id="extracurricular" 
      sx={{ 
        py: { xs: 6, md: 12 }, // Less padding on mobile
        backgroundColor: 'background.paper',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <SectionTitle
              variant="h2"
              component="h2"
              sx={{ 
                fontWeight: 700,
                color: 'primary.main',
                fontSize: { xs: '1.75rem', md: '2.25rem' }, // Smaller on mobile
              }}
            >
              Extra Curricular
            </SectionTitle>
          </motion.div>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mb: { xs: 4, md: 6 }, // Less margin on mobile
          width: '100%',
          px: { xs: 0, md: 2 },
        }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ width: '100%' }}
          >
            <StyledTabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="extra curricular tabs"
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabScrollButtonProps={{
                sx: {
                  '&.Mui-disabled': { opacity: 0 },
                  padding: 0,
                }
              }}
              TabIndicatorProps={{
                sx: { display: isMobile ? 'none' : 'block' }
              }}
              centered={!isMobile}
              isMobile={isMobile}
              sx={{ 
                maxWidth: '100%',
                '.MuiTabScrollButton-root': {
                  height: '100%',
                  width: '24px',
                  opacity: 0.8,
                }
              }}
            >
              {activities.map((activity, index) => (
                <StyledTab 
                  key={activity.id} 
                  label={activity.title} 
                  icon={activity.icon} 
                  iconPosition="start"
                  isMobile={isMobile}
                />
              ))}
            </StyledTabs>
          </motion.div>
        </Box>

        <AnimatePresence mode="wait">
          <motion.div
            key={tabValue}
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Grid container spacing={{ xs: 2, md: 6 }}>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <StyledImage
                    image={activities[tabValue].image}
                    title={activities[tabValue].title}
                  />
                </StyledCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: { xs: 'flex-start', md: 'center' },
                  mt: { xs: 1, md: 0 } // Less margin on mobile
                }}>
                  <CardContent sx={{ padding: { xs: '16px', md: '24px' } }}> {/* Less padding on mobile */}
                    <Typography 
                      variant="h4" 
                      component="h3" 
                      gutterBottom
                      sx={{ 
                        color: 'primary.main',
                        fontWeight: 600,
                        mb: { xs: 1.5, md: 3 }, // Less margin on mobile
                        fontSize: { xs: '1.3rem', md: '2rem' } // Smaller on mobile
                      }}
                    >
                      {activities[tabValue].title}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.6, // Better line height for readability
                        fontSize: { xs: '0.9rem', md: '1.1rem' }, // Smaller font on mobile
                        mt: { xs: 1, md: 2 }
                      }}
                    >
                      {activities[tabValue].description}
                    </Typography>
                  </CardContent>
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        </AnimatePresence>
      </Container>
    </Box>
  );
};

export default ExtraCurricular; 